import React, { useState, useEffect, ChangeEvent } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { categories, Category, SubCategory, findCategoryAndSubcategory } from '../../../routes/toolsRoute';
import SEO from '../../../components/SEO';
import { ArrowRight, ChevronRight, Search } from 'lucide-react';
import { trackEvent } from '../../../utils/GoogleAnalytics';
import { pushGTMEvent } from '../../../utils/GoogleTagManager';

// Import components directly 

// VIEWER
import HtmlViewer from './Viewer/HtmlViewer';
import CssViewer from './Viewer/CssViewer';
// import JavaScriptViewer from './Viewer/JavaScriptViewer';



// Minifiers
import CssMinifier from './Minifier/CssMinifier';
import JavaScriptMinifier from './Minifier/JavaScriptMinifier';
import HTMLMinifier from './Minifier/HTMLMinifier';


// String Utilities
import CaseConverter from './StringUtilities/CaseConverter';
import ReverseString from './StringUtilities/ReverseString';
import StringLengthCounter from './StringUtilities/StringLengthCounter';
import RemoveDuplicateLines from './StringUtilities/RemoveDuplicateLines';
import SortLines from './StringUtilities/SortLines';
import FindReplace from './StringUtilities/FindReplace';
import TextDifferenceChecker from './StringUtilities/TextDifferenceChecker';
import StringSplitter from './StringUtilities/StringSplitter';
import StringJoiner from './StringUtilities/StringJoiner';
import WhitespaceRemover from './StringUtilities/WhitespaceRemover';
import LineNumberer from './StringUtilities/LineNumberer';
import TextExtractor from './StringUtilities/TextExtractor';
import LoremIpsumGenerator from './StringUtilities/LoremIpsumGenerator';
import SlugGenerator from './StringUtilities/SlugGenerator';
import CamelCaseConverter from './StringUtilities/CamelCaseConverter';


// Escape Unescape
import HtmlEscapeUnescapeTool from './EscapeUnescape/HtmlEscapeUnescapeTool';
import XmlEscapeUnescapeTool from './EscapeUnescape/XmlEscapeUnescapeTool';
import JsonEscapeUnescapeTool from './EscapeUnescape/JsonEscapeUnescapeTool';
import JavaScriptEscapeUnescapeTool from './EscapeUnescape/JavaScriptEscapeUnescapeTool';
import SqlEscapeUnescapeTool from './EscapeUnescape/SqlEscapeUnescapeTool';
import RegexEscapeUnescapeTool from './EscapeUnescape/RegexEscapeUnescapeTool';
import UrlEscapeUnescapeTool from './EscapeUnescape/UrlEscapeUnescapeTool';
import UnicodeEscapeUnescapeTool from './EscapeUnescape/UnicodeEscapeUnescapeTool';
import CppEscapeUnescapeTool from './EscapeUnescape/CppEscapeUnescapeTool';
import JavaEscapeUnescapeTool from './EscapeUnescape/JavaEscapeUnescapeTool';
import PythonEscapeUnescapeTool from './EscapeUnescape/PythonEscapeUnescapeTool';
import PhpEscapeUnescapeTool from './EscapeUnescape/PhpEscapeUnescapeTool';
import BashEscapeUnescapeTool from './EscapeUnescape/BashEscapeUnescapeTool';


// Number Utilities
import NumberBaseConverterTool from './NumberUtilities/NumberBaseConverterTool';
import BinaryConverterTool from './NumberUtilities/BinaryConverterTool';
import RomanNumeralConverterTool from './NumberUtilities/RomanNumeralConverterTool';
import FractionCalculatorTool from './NumberUtilities/FractionCalculatorTool';
import PercentageCalculatorTool from './NumberUtilities/PercentageCalculatorTool';
import ScientificCalculatorTool from './NumberUtilities/ScientificCalculatorTool';
import PrimeNumberCheckerTool from './NumberUtilities/PrimeNumberCheckerTool';
import FibonacciGeneratorTool from './NumberUtilities/FibonacciGeneratorTool';
import FactorialCalculatorTool from './NumberUtilities/FactorialCalculatorTool';
import GCDLCMCalculatorTool from './NumberUtilities/GCDLCMCalculatorTool';
import NumberFormatterTool from './NumberUtilities/NumberFormatterTool';
import RandomNumberGeneratorTool from './NumberUtilities/RandomNumberGeneratorTool';
import StatisticsCalculator from './NumberUtilities/StatisticsCalculator';


// Unit Converter
import LengthConverter from './UnitConverter/LengthConverter';
import WeightConverter from './UnitConverter/WeightConverter';
import TemperatureConverter from './UnitConverter/TemperatureConverter';
import AreaConverter from './UnitConverter/AreaConverter';
import VolumeConverter from './UnitConverter/VolumeConverter';
import SpeedConverter from './UnitConverter/SpeedConverter';


// Color Converter
import HexToRgbConverter from './ColorConverter/HexToRgbConverter';


// Chiper
import CaesarCipherApp from './Cipher/CaesarCipherApp';
import VigenereCipherApp from './Cipher/VigenereCipherApp';
import AtbashCipherApp from './Cipher/AtbashCipherApp';
import ROT13CipherApp from './Cipher/ROT13CipherApp';
import ROT47CipherApp from './Cipher/ROT47CipherApp';
import PlayfairCipherApp from './Cipher/PlayfairCipherApp';
import HillCipherApp from './Cipher/HillCipherApp';
import RailFenceCipherApp from './Cipher/RailFenceCipherApp';
import ColumnarTranspositionCipherApp from './Cipher/ColumnarTranspositionCipherApp';
import AffineCipherApp from './Cipher/AffineCipherApp';
import BaconCipherApp from './Cipher/BaconCipherApp';
import PolybiusSquareApp from './Cipher/PolybiusSquareApp';
import FourSquareCipherApp from './Cipher/FourSquareCipherApp';
import TwoSquareCipherApp from './Cipher/TwoSquareCipherApp';
import BeaufortCipherApp from './Cipher/BeaufortCipherApp';
import GronsfeldCipherApp from './Cipher/GronsfeldCipherApp';
import PortaCipherApp from './Cipher/PortaCipherApp';

// Steganography

import ImageSteganographyApp from './Steganography/ImageSteganographyApp';
import TextSteganographyApp from './Steganography/TextSteganographyApp';

// Encrype and Decrypt
import TextEncryptDecryptApp from './EncryptDecrypt/TextEncryptDecryptApp';
import JsonEncryptDecryptApp from './EncryptDecrypt/JsonEncryptDecryptApp';
import FileEncryptDecrypt from './EncryptDecrypt/FileEncryptDecrypt';
import DESEncryptDecrypt from './EncryptDecrypt/DESEncryptDecrypt';
import AESEncryptDecrypt from './EncryptDecrypt/AESEncryptDecrypt';
import TripleDESEncryptDecrypt from './EncryptDecrypt/TripleDESEncryptDecrypt';
import BlowfishEncryptDecrypt from './EncryptDecrypt/BlowfishEncryptDecrypt';
import TwofishEncryptDecrypt from './EncryptDecrypt/TwofishEncryptDecrypt';
import RC4EncryptDecrypt from './EncryptDecrypt/RC4EncryptDecrypt';
import ChaCha20EncryptDecrypt from './EncryptDecrypt/ChaCha20EncryptDecrypt';
import RSAEncryptDecrypt from './EncryptDecrypt/RSAEncryptDecrypt';
import ECCEncryptDecrypt from './EncryptDecrypt/ECCEncryptDecrypt';
import DSAEncryptDecrypt from './EncryptDecrypt/DSAEncryptDecrypt';
import ElGamalEncryptDecrypt from './EncryptDecrypt/ElGamalEncryptDecrypt';
import ZipEncryptDecrypt from './EncryptDecrypt/ZipEncryptDecrypt';
import PDFEncryptDecrypt from './EncryptDecrypt/PDFEncryptDecrypt';
import ImageEncryptDecrypt from './EncryptDecrypt/ImageEncryptDecrypt';
import DatabaseEncryptDecrypt from './EncryptDecrypt/DatabaseEncryptDecrypt';


// Generator
import PasswordGenerator from './Generator/PasswordGenerator';
import RandomPasswordGenerator from './Generator/RandomPasswordGenerator';
import SecurePasswordGenerator from './Generator/SecurePasswordGenerator';
import ApiKeyGenerator from './Generator/ApiKeyGenerator';
import UuidGenerator from './Generator/UuidGenerator';
import OAuthTokenGenerator from './Generator/OAuthTokenGenerator';
import RandomStringGenerator from './Generator/RandomStringGenerator';
import QRCodeGenerator from './Generator/QRCodeGenerator';
import BarcodeGenerator from './Generator/BarcodeGenerator';
import MD5HashGenerator from './Generator/MD5HashGenerator';
import SHA1HashGenerator from './Generator/SHA1HashGenerator';
import SHA256HashGenerator from './Generator/SHA256HashGenerator';
import SHA384HashGenerator from './Generator/SHA384HashGenerator';
import SHA512HashGenerator from './Generator/SHA512HashGenerator';
import SHA3HashGenerator from './Generator/SHA3HashGenerator';
import BLAKE2HashGenerator from './Generator/BLAKE2HashGenerator';
import WhirlpoolHashGenerator from './Generator/WhirlpoolHashGenerator';
import RIPEMDHashGenerator from './Generator/RIPEMDHashGenerator';
import BcryptHashGenerator from './Generator/BcryptHashGenerator';
import ScryptHashGenerator from './Generator/ScryptHashGenerator';
import Argon2HashGenerator from './Generator/Argon2HashGenerator';
import PBKDF2HashGenerator from './Generator/PBKDF2HashGenerator';
import RSAKeyGenerator from './Generator/RSAKeyGenerator';
import SSHKeyGenerator from './Generator/SSHKeyGenerator';




import WorkInProgress from '../../common/WorkInProgress';


const converterComponents: Record<string, Record<string, React.ComponentType<any>>> = {
  'viewers': {
    'html-viewer': HtmlViewer,
    'css-viewer': WorkInProgress,
    'javascript-viewer': WorkInProgress,
    'json-viewer': WorkInProgress,
    'xml-viewer': WorkInProgress,
    'yaml-viewer': WorkInProgress,
    'markdown-viewer': WorkInProgress,
    'csv-viewer': WorkInProgress,
    'sql-viewer': WorkInProgress,
    'php-viewer': WorkInProgress,
    'python-viewer': WorkInProgress,
    'java-viewer': WorkInProgress,
    'c-cpp-viewer': WorkInProgress,
    'go-viewer': WorkInProgress,
    'rust-viewer': WorkInProgress,
    'typescript-viewer': WorkInProgress,
    'react-viewer': WorkInProgress,
    'vue-viewer': WorkInProgress,
    'angular-viewer': WorkInProgress,
    'dockerfile-viewer': WorkInProgress,
    'log-viewer': WorkInProgress,
    'image-viewer': WorkInProgress,
    'pdf-viewer': WorkInProgress,
    'text-viewer': WorkInProgress,
  },
  'minifiers': {
    'css-minifier': CssMinifier,
    'js-minifier': JavaScriptMinifier,
    'html-minifier': HTMLMinifier,
  },
  'color-converters': {
    'hex-to-rgb': HexToRgbConverter,
    'rgb-to-hex': WorkInProgress,
    'hsl-to-rgb': WorkInProgress,
    'rgb-to-hsl': WorkInProgress,
    'hsv-to-rgb': WorkInProgress,
    'rgb-to-hsv': WorkInProgress,
    'cmyk-to-rgb': WorkInProgress,
    'rgb-to-cmyk': WorkInProgress,
    'lab-to-rgb': WorkInProgress,
    'rgb-to-lab': WorkInProgress,
    'xyz-to-rgb': WorkInProgress,
    'rgb-to-xyz': WorkInProgress,
    'color-palette-generator': WorkInProgress,
    'color-picker': WorkInProgress,
    'gradient-generator': WorkInProgress,
    'color-blindness-simulator': WorkInProgress,
    'contrast-checker': WorkInProgress,
  },
  'unit-converter': {
    'length-converter': LengthConverter,
    'weight-converter': WeightConverter,
    'temperature-converter': TemperatureConverter,
    'area-converter': AreaConverter,
    'volume-converter': VolumeConverter,
    'speed-converter': SpeedConverter,
    'pressure-converter': WorkInProgress,
    'energy-converter': WorkInProgress,
    'power-converter': WorkInProgress,
    'frequency-converter': WorkInProgress,
    'time-converter': WorkInProgress,
    'data-storage-converter': WorkInProgress,
    'currency-converter': WorkInProgress,
    'angle-converter': WorkInProgress,
    'fuel-efficiency-converter': WorkInProgress,
    'cooking-converter': WorkInProgress,
  },
  'base64-tools': {
    'base64-encode': WorkInProgress,
    'base64-decode': WorkInProgress,
    'base64-image-encoder': WorkInProgress,
    'base64-image-decoder': WorkInProgress,
    'base64-file-encoder': WorkInProgress,
    'base64-file-decoder': WorkInProgress,
    'base64-url-safe': WorkInProgress,
    'base32-encode-decode': WorkInProgress,
    'base16-encode-decode': WorkInProgress,
    'base85-encode-decode': WorkInProgress,
    'base58-encode-decode': WorkInProgress,
    'data-uri-generator': WorkInProgress,
  },
  'string-utilities': {
    'case-converter': CaseConverter,
    'reverse-string': ReverseString,
    'string-length-counter': StringLengthCounter,
    'word-counter': StringLengthCounter,
    'character-counter': StringLengthCounter,
    'remove-duplicates': RemoveDuplicateLines,
    'sort-lines': SortLines,
    'find-replace': FindReplace,
    'text-diff': TextDifferenceChecker,
    'string-splitter': StringSplitter,
    'string-joiner': StringJoiner,
    'whitespace-remover': WhitespaceRemover,
    'line-numberer': LineNumberer,
    'text-extractor': TextExtractor,
    'lorem-ipsum-generator': LoremIpsumGenerator,
    'slug-generator': SlugGenerator,
    'camel-case-converter': CamelCaseConverter,
    'snake-case-converter': CamelCaseConverter,
    'kebab-case-converter': CamelCaseConverter,
  },
  'escape-unescape': {
    'html-escape-unescape': HtmlEscapeUnescapeTool,
    'xml-escape-unescape': XmlEscapeUnescapeTool,
    'json-escape-unescape': JsonEscapeUnescapeTool,
    'javascript-escape-unescape': JavaScriptEscapeUnescapeTool,
    'sql-escape-unescape': SqlEscapeUnescapeTool,
    'regex-escape-unescape': RegexEscapeUnescapeTool,
    'url-escape-unescape': UrlEscapeUnescapeTool,
    'unicode-escape-unescape': UnicodeEscapeUnescapeTool,
    'c-escape-unescape': CppEscapeUnescapeTool,
    'java-escape-unescape': JavaEscapeUnescapeTool,
    'python-escape-unescape': PythonEscapeUnescapeTool,
    'php-escape-unescape': PhpEscapeUnescapeTool,
    'bash-escape-unescape': BashEscapeUnescapeTool,
  },
  'number-utilities': {
    'number-base-converter': NumberBaseConverterTool,
    'binary-converter': BinaryConverterTool,
    'octal-converter': BinaryConverterTool,
    'hexadecimal-converter': BinaryConverterTool,
    'roman-numeral-converter': RomanNumeralConverterTool,
    'fraction-calculator': FractionCalculatorTool,
    'percentage-calculator': PercentageCalculatorTool,
    'scientific-calculator': ScientificCalculatorTool,
    'prime-checker': PrimeNumberCheckerTool,
    'fibonacci-generator': FibonacciGeneratorTool,
    'factorial-calculator': FactorialCalculatorTool,
    'gcd-lcm-calculator': GCDLCMCalculatorTool,
    'number-formatter': NumberFormatterTool,
    'random-number-generator': RandomNumberGeneratorTool,
    'statistics-calculator': StatisticsCalculator,
  },
  'encode-decode': {
    'base64-encode-decode': WorkInProgress,
    'base32-encode-decode': WorkInProgress,
    'url-encode-decode': WorkInProgress,
    'html-encode-decode': WorkInProgress,
    'unicode-encode-decode': WorkInProgress,
    'hex-encode-decode': WorkInProgress,
    'binary-encode-decode': WorkInProgress,
    'ascii-encode-decode': WorkInProgress,
    'morse-code-encode-decode': WorkInProgress,
    'jwt-encode-decode': WorkInProgress,
    'json-encode-decode': WorkInProgress,
    'xml-encode-decode': WorkInProgress,
    'csv-encode-decode': WorkInProgress,
  },
  'generator': {
    'password-generator': PasswordGenerator,
    'random-password-generator': RandomPasswordGenerator,
    'secure-password-generator': SecurePasswordGenerator,
    'api-key-generator': ApiKeyGenerator,
    'uuid-generator': UuidGenerator,
    'oauth-token-generator': OAuthTokenGenerator,
    'random-string-generator': RandomStringGenerator,
    'qr-code-generator': QRCodeGenerator,
    'barcode-generator': BarcodeGenerator,
    'md5-hash-generator': MD5HashGenerator,
    'sha1-hash-generator': SHA1HashGenerator,
    'sha256-hash-generator': SHA256HashGenerator,
    'sha384-hash-generator': SHA384HashGenerator,
    'sha512-hash-generator': SHA512HashGenerator,
    'sha3-hash-generator': SHA3HashGenerator,
    'blake2-hash-generator': BLAKE2HashGenerator,
    'whirlpool-hash-generator': WhirlpoolHashGenerator,
    'ripemd-hash-generator': RIPEMDHashGenerator,
    'bcrypt-hash-generator': BcryptHashGenerator,
    'scrypt-hash-generator': ScryptHashGenerator,
    'argon2-hash-generator': Argon2HashGenerator,
    'pbkdf2-hash-generator': PBKDF2HashGenerator,
    'rsa-key-generator': RSAKeyGenerator,
    'ssh-key-generator': SSHKeyGenerator,
  },
  'encrypt-decrypt': {
    'text-encrypt-decrypt': TextEncryptDecryptApp,
    'json-encrypt-decrypt': JsonEncryptDecryptApp,
    'file-encrypt-decrypt': FileEncryptDecrypt,
    'aes-encrypt-decrypt': AESEncryptDecrypt,
    'des-encrypt-decrypt': DESEncryptDecrypt,
    '3des-encrypt-decrypt': TripleDESEncryptDecrypt,
    'blowfish-encrypt-decrypt': BlowfishEncryptDecrypt,
    'twofish-encrypt-decrypt': TwofishEncryptDecrypt,
    'rc4-encrypt-decrypt': RC4EncryptDecrypt,
    'chacha20-encrypt-decrypt': ChaCha20EncryptDecrypt,
    'rsa-encrypt-decrypt': RSAEncryptDecrypt,
    'ecc-encrypt-decrypt': ECCEncryptDecrypt,
    'dsa-encrypt-decrypt': DSAEncryptDecrypt,
    'elgamal-encrypt-decrypt': ElGamalEncryptDecrypt,
    'zip-encrypt-decrypt': ZipEncryptDecrypt,
    'pdf-encrypt-decrypt': PDFEncryptDecrypt,
    'image-encrypt-decrypt': ImageEncryptDecrypt,
    'database-encrypt-decrypt': DatabaseEncryptDecrypt,
  },
  'cipher': {
    'caesar-cipher': CaesarCipherApp,
    'vigenere-cipher': VigenereCipherApp,
    'atbash-cipher': AtbashCipherApp,
    'rot13-cipher': ROT13CipherApp,
    'rot47-cipher': ROT47CipherApp,
    'playfair-cipher': PlayfairCipherApp,
    'hill-cipher': HillCipherApp,
    'rail-fence-cipher': RailFenceCipherApp,
    'columnar-transposition': ColumnarTranspositionCipherApp,
    'affine-cipher': AffineCipherApp,
    'bacon-cipher': BaconCipherApp,
    'polybius-square': PolybiusSquareApp,
    'four-square-cipher': FourSquareCipherApp,
    'two-square-cipher': TwoSquareCipherApp,
    'beaufort-cipher': BeaufortCipherApp,
    'gronsfeld-cipher': GronsfeldCipherApp,
    'porta-cipher': PortaCipherApp,
  },
  'steganography': {
    'image-steganography': ImageSteganographyApp,
    'text-steganography': TextSteganographyApp,
    'audio-steganography': WorkInProgress,
    'video-steganography': WorkInProgress,
    'pdf-steganography': WorkInProgress,
    'lsb-steganography': WorkInProgress,
    'dct-steganography': WorkInProgress,
    'whitespace-steganography': WorkInProgress,
    'unicode-steganography': WorkInProgress,
    'metadata-steganography': WorkInProgress,
  },
  'other-security-tools': {
    'password-strength-checker': WorkInProgress,
    'hash-cracker': WorkInProgress,
    'hash-identifier': WorkInProgress,
    'digital-signature-verifier': WorkInProgress,
    'certificate-parser': WorkInProgress,
    'ssl-checker': WorkInProgress,
    'checksum-calculator': WorkInProgress,
    'entropy-calculator': WorkInProgress,
    'random-number-generator': WorkInProgress,
    'secure-delete': WorkInProgress,
    'password-policy-checker': WorkInProgress,
    'breach-checker': WorkInProgress,
    'network-scanner': WorkInProgress,
    'vulnerability-scanner': WorkInProgress,
    'malware-scanner': WorkInProgress,
    'phishing-detector': WorkInProgress,
  },
};

const directRouteMap: Record<string, { categoryId: string; subcategoryId: string; component: React.ComponentType<any> | null }> = {
  // Viewers
  'html-viewer': { categoryId: 'viewers', subcategoryId: 'html-viewer', component: HtmlViewer },
  'css-viewer': { categoryId: 'viewers', subcategoryId: 'css-viewer', component: WorkInProgress },
  'javascript-viewer': { categoryId: 'viewers', subcategoryId: 'javascript-viewer', component: WorkInProgress },
  'json-viewer': { categoryId: 'viewers', subcategoryId: 'json-viewer', component: WorkInProgress },
  'xml-viewer': { categoryId: 'viewers', subcategoryId: 'xml-viewer', component: WorkInProgress },
  'yaml-viewer': { categoryId: 'viewers', subcategoryId: 'yaml-viewer', component: WorkInProgress },
  'markdown-viewer': { categoryId: 'viewers', subcategoryId: 'markdown-viewer', component: WorkInProgress },
  'csv-viewer': { categoryId: 'viewers', subcategoryId: 'csv-viewer', component: WorkInProgress },
  'sql-viewer': { categoryId: 'viewers', subcategoryId: 'sql-viewer', component: WorkInProgress },
  'php-viewer': { categoryId: 'viewers', subcategoryId: 'php-viewer', component: WorkInProgress },
  'python-viewer': { categoryId: 'viewers', subcategoryId: 'python-viewer', component: WorkInProgress },
  'java-viewer': { categoryId: 'viewers', subcategoryId: 'java-viewer', component: WorkInProgress },
  'c-cpp-viewer': { categoryId: 'viewers', subcategoryId: 'c-cpp-viewer', component: WorkInProgress },
  'go-viewer': { categoryId: 'viewers', subcategoryId: 'go-viewer', component: WorkInProgress },
  'rust-viewer': { categoryId: 'viewers', subcategoryId: 'rust-viewer', component: WorkInProgress },
  'typescript-viewer': { categoryId: 'viewers', subcategoryId: 'typescript-viewer', component: WorkInProgress },
  'react-viewer': { categoryId: 'viewers', subcategoryId: 'react-viewer', component: WorkInProgress },
  'vue-viewer': { categoryId: 'viewers', subcategoryId: 'vue-viewer', component: WorkInProgress },
  'angular-viewer': { categoryId: 'viewers', subcategoryId: 'angular-viewer', component: WorkInProgress },
  'dockerfile-viewer': { categoryId: 'viewers', subcategoryId: 'dockerfile-viewer', component: WorkInProgress },
  'log-viewer': { categoryId: 'viewers', subcategoryId: 'log-viewer', component: WorkInProgress },
  'image-viewer': { categoryId: 'viewers', subcategoryId: 'image-viewer', component: WorkInProgress },
  'pdf-viewer': { categoryId: 'viewers', subcategoryId: 'pdf-viewer', component: WorkInProgress },
  'text-viewer': { categoryId: 'viewers', subcategoryId: 'text-viewer', component: WorkInProgress },

  // Minifiers
  'css-minifier': { categoryId: 'minifiers', subcategoryId: 'css-minifier', component: CssMinifier },
  'js-minifier': { categoryId: 'minifiers', subcategoryId: 'js-minifier', component: JavaScriptMinifier },
  'html-minifier': { categoryId: 'minifiers', subcategoryId: 'html-minifier', component: HTMLMinifier },

  // Color Converters
  'hex-to-rgb': { categoryId: 'color-converters', subcategoryId: 'hex-to-rgb', component: HexToRgbConverter },
  'rgb-to-hex': { categoryId: 'color-converters', subcategoryId: 'rgb-to-hex', component: WorkInProgress },
  'hsl-to-rgb': { categoryId: 'color-converters', subcategoryId: 'hsl-to-rgb', component: WorkInProgress },
  'rgb-to-hsl': { categoryId: 'color-converters', subcategoryId: 'rgb-to-hsl', component: WorkInProgress },
  'hsv-to-rgb': { categoryId: 'color-converters', subcategoryId: 'hsv-to-rgb', component: WorkInProgress },
  'rgb-to-hsv': { categoryId: 'color-converters', subcategoryId: 'rgb-to-hsv', component: WorkInProgress },
  'cmyk-to-rgb': { categoryId: 'color-converters', subcategoryId: 'cmyk-to-rgb', component: WorkInProgress },
  'rgb-to-cmyk': { categoryId: 'color-converters', subcategoryId: 'rgb-to-cmyk', component: WorkInProgress },
  'lab-to-rgb': { categoryId: 'color-converters', subcategoryId: 'lab-to-rgb', component: WorkInProgress },
  'rgb-to-lab': { categoryId: 'color-converters', subcategoryId: 'rgb-to-lab', component: WorkInProgress },
  'xyz-to-rgb': { categoryId: 'color-converters', subcategoryId: 'xyz-to-rgb', component: WorkInProgress },
  'rgb-to-xyz': { categoryId: 'color-converters', subcategoryId: 'rgb-to-xyz', component: WorkInProgress },
  'color-palette-generator': { categoryId: 'color-converters', subcategoryId: 'color-palette-generator', component: WorkInProgress },
  'color-picker': { categoryId: 'color-converters', subcategoryId: 'color-picker', component: WorkInProgress },
  'gradient-generator': { categoryId: 'color-converters', subcategoryId: 'gradient-generator', component: WorkInProgress },
  'color-blindness-simulator': { categoryId: 'color-converters', subcategoryId: 'color-blindness-simulator', component: WorkInProgress },
  'contrast-checker': { categoryId: 'color-converters', subcategoryId: 'contrast-checker', component: WorkInProgress },

  // Unit Converter
  'length-converter': { categoryId: 'unit-converter', subcategoryId: 'length-converter', component: LengthConverter },
  'weight-converter': { categoryId: 'unit-converter', subcategoryId: 'weight-converter', component: WeightConverter },
  'temperature-converter': { categoryId: 'unit-converter', subcategoryId: 'temperature-converter', component: TemperatureConverter },
  'area-converter': { categoryId: 'unit-converter', subcategoryId: 'area-converter', component: AreaConverter },
  'volume-converter': { categoryId: 'unit-converter', subcategoryId: 'volume-converter', component: VolumeConverter },
  'speed-converter': { categoryId: 'unit-converter', subcategoryId: 'speed-converter', component: SpeedConverter },
  'pressure-converter': { categoryId: 'unit-converter', subcategoryId: 'pressure-converter', component: WorkInProgress },
  'energy-converter': { categoryId: 'unit-converter', subcategoryId: 'energy-converter', component: WorkInProgress },
  'power-converter': { categoryId: 'unit-converter', subcategoryId: 'power-converter', component: WorkInProgress },
  'frequency-converter': { categoryId: 'unit-converter', subcategoryId: 'frequency-converter', component: WorkInProgress },
  'time-converter': { categoryId: 'unit-converter', subcategoryId: 'time-converter', component: WorkInProgress },
  'data-storage-converter': { categoryId: 'unit-converter', subcategoryId: 'data-storage-converter', component: WorkInProgress },
  'currency-converter': { categoryId: 'unit-converter', subcategoryId: 'currency-converter', component: WorkInProgress },
  'angle-converter': { categoryId: 'unit-converter', subcategoryId: 'angle-converter', component: WorkInProgress },
  'fuel-efficiency-converter': { categoryId: 'unit-converter', subcategoryId: 'fuel-efficiency-converter', component: WorkInProgress },
  'cooking-converter': { categoryId: 'unit-converter', subcategoryId: 'cooking-converter', component: WorkInProgress },

  // Base64 Tools
  'base64-encode': { categoryId: 'base64-tools', subcategoryId: 'base64-encode', component: WorkInProgress },
  'base64-decode': { categoryId: 'base64-tools', subcategoryId: 'base64-decode', component: WorkInProgress },
  'base64-image-encoder': { categoryId: 'base64-tools', subcategoryId: 'base64-image-encoder', component: WorkInProgress },
  'base64-image-decoder': { categoryId: 'base64-tools', subcategoryId: 'base64-image-decoder', component: WorkInProgress },
  'base64-file-encoder': { categoryId: 'base64-tools', subcategoryId: 'base64-file-encoder', component: WorkInProgress },
  'base64-file-decoder': { categoryId: 'base64-tools', subcategoryId: 'base64-file-decoder', component: WorkInProgress },
  'base64-url-safe': { categoryId: 'base64-tools', subcategoryId: 'base64-url-safe', component: WorkInProgress },
  'base32-encode-decode': { categoryId: 'base64-tools', subcategoryId: 'base32-encode-decode', component: WorkInProgress },
  'base16-encode-decode': { categoryId: 'base64-tools', subcategoryId: 'base16-encode-decode', component: WorkInProgress },
  'base85-encode-decode': { categoryId: 'base64-tools', subcategoryId: 'base85-encode-decode', component: WorkInProgress },
  'base58-encode-decode': { categoryId: 'base64-tools', subcategoryId: 'base58-encode-decode', component: WorkInProgress },
  'data-uri-generator': { categoryId: 'base64-tools', subcategoryId: 'data-uri-generator', component: WorkInProgress },

  // String Utilities
  'case-converter': { categoryId: 'string-utilities', subcategoryId: 'case-converter', component: CaseConverter },
  'reverse-string': { categoryId: 'string-utilities', subcategoryId: 'reverse-string', component: ReverseString },
  'string-length-counter': { categoryId: 'string-utilities', subcategoryId: 'string-length-counter', component: StringLengthCounter },
  'word-counter': { categoryId: 'string-utilities', subcategoryId: 'word-counter', component: StringLengthCounter },
  'character-counter': { categoryId: 'string-utilities', subcategoryId: 'character-counter', component: StringLengthCounter },
  'remove-duplicates': { categoryId: 'string-utilities', subcategoryId: 'remove-duplicates', component: RemoveDuplicateLines },
  'sort-lines': { categoryId: 'string-utilities', subcategoryId: 'sort-lines', component: SortLines },
  'find-replace': { categoryId: 'string-utilities', subcategoryId: 'find-replace', component: FindReplace },
  'text-diff': { categoryId: 'string-utilities', subcategoryId: 'text-diff', component: TextDifferenceChecker },
  'string-splitter': { categoryId: 'string-utilities', subcategoryId: 'string-splitter', component: StringSplitter },
  'string-joiner': { categoryId: 'string-utilities', subcategoryId: 'string-joiner', component: StringJoiner },
  'whitespace-remover': { categoryId: 'string-utilities', subcategoryId: 'whitespace-remover', component: WhitespaceRemover },
  'line-numberer': { categoryId: 'string-utilities', subcategoryId: 'line-numberer', component: LineNumberer },
  'text-extractor': { categoryId: 'string-utilities', subcategoryId: 'text-extractor', component: TextExtractor },
  'lorem-ipsum-generator': { categoryId: 'string-utilities', subcategoryId: 'lorem-ipsum-generator', component: LoremIpsumGenerator },
  'slug-generator': { categoryId: 'string-utilities', subcategoryId: 'slug-generator', component: SlugGenerator },
  'camel-case-converter': { categoryId: 'string-utilities', subcategoryId: 'camel-case-converter', component: CamelCaseConverter },
  'snake-case-converter': { categoryId: 'string-utilities', subcategoryId: 'snake-case-converter', component: CamelCaseConverter },
  'kebab-case-converter': { categoryId: 'string-utilities', subcategoryId: 'kebab-case-converter', component: CamelCaseConverter },

  // Escape Unescape
  'html-escape-unescape': { categoryId: 'escape-unescape', subcategoryId: 'html-escape-unescape', component: HtmlEscapeUnescapeTool },
  'xml-escape-unescape': { categoryId: 'escape-unescape', subcategoryId: 'xml-escape-unescape', component: XmlEscapeUnescapeTool },
  'json-escape-unescape': { categoryId: 'escape-unescape', subcategoryId: 'json-escape-unescape', component: JsonEscapeUnescapeTool },
  'javascript-escape-unescape': { categoryId: 'escape-unescape', subcategoryId: 'javascript-escape-unescape', component: JavaScriptEscapeUnescapeTool },
  'sql-escape-unescape': { categoryId: 'escape-unescape', subcategoryId: 'sql-escape-unescape', component: SqlEscapeUnescapeTool },
  'regex-escape-unescape': { categoryId: 'escape-unescape', subcategoryId: 'regex-escape-unescape', component: RegexEscapeUnescapeTool },
  'url-escape-unescape': { categoryId: 'escape-unescape', subcategoryId: 'url-escape-unescape', component: UrlEscapeUnescapeTool },
  'unicode-escape-unescape': { categoryId: 'escape-unescape', subcategoryId: 'unicode-escape-unescape', component: UnicodeEscapeUnescapeTool },
  'c-escape-unescape': { categoryId: 'escape-unescape', subcategoryId: 'c-escape-unescape', component: CppEscapeUnescapeTool },
  'java-escape-unescape': { categoryId: 'escape-unescape', subcategoryId: 'java-escape-unescape', component: JavaEscapeUnescapeTool },
  'python-escape-unescape': { categoryId: 'escape-unescape', subcategoryId: 'python-escape-unescape', component: PythonEscapeUnescapeTool },
  'php-escape-unescape': { categoryId: 'escape-unescape', subcategoryId: 'php-escape-unescape', component: PhpEscapeUnescapeTool },
  'bash-escape-unescape': { categoryId: 'escape-unescape', subcategoryId: 'bash-escape-unescape', component: BashEscapeUnescapeTool },

  // Number Utilities
  'number-base-converter': { categoryId: 'number-utilities', subcategoryId: 'number-base-converter', component: NumberBaseConverterTool },
  'binary-converter': { categoryId: 'number-utilities', subcategoryId: 'binary-converter', component: BinaryConverterTool },
  'octal-converter': { categoryId: 'number-utilities', subcategoryId: 'octal-converter', component: BinaryConverterTool },
  'hexadecimal-converter': { categoryId: 'number-utilities', subcategoryId: 'hexadecimal-converter', component: BinaryConverterTool },
  'roman-numeral-converter': { categoryId: 'number-utilities', subcategoryId: 'roman-numeral-converter', component: RomanNumeralConverterTool },
  'fraction-calculator': { categoryId: 'number-utilities', subcategoryId: 'fraction-calculator', component: FractionCalculatorTool },
  'percentage-calculator': { categoryId: 'number-utilities', subcategoryId: 'percentage-calculator', component: PercentageCalculatorTool },
  'scientific-calculator': { categoryId: 'number-utilities', subcategoryId: 'scientific-calculator', component: ScientificCalculatorTool },
  'prime-checker': { categoryId: 'number-utilities', subcategoryId: 'prime-checker', component: PrimeNumberCheckerTool },
  'fibonacci-generator': { categoryId: 'number-utilities', subcategoryId: 'fibonacci-generator', component: FibonacciGeneratorTool },
  'factorial-calculator': { categoryId: 'number-utilities', subcategoryId: 'factorial-calculator', component: FactorialCalculatorTool },
  'gcd-lcm-calculator': { categoryId: 'number-utilities', subcategoryId: 'gcd-lcm-calculator', component: GCDLCMCalculatorTool },
  'number-formatter': { categoryId: 'number-utilities', subcategoryId: 'number-formatter', component: NumberFormatterTool },
  'random-number-generator': { categoryId: 'number-utilities', subcategoryId: 'random-number-generator', component: RandomNumberGeneratorTool },
  'statistics-calculator': { categoryId: 'number-utilities', subcategoryId: 'statistics-calculator', component: StatisticsCalculator },

  // Encode Decode
  'url-encode-decode': { categoryId: 'encode-decode', subcategoryId: 'url-encode-decode', component: WorkInProgress },
  'html-encode-decode': { categoryId: 'encode-decode', subcategoryId: 'html-encode-decode', component: WorkInProgress },
  'unicode-encode-decode': { categoryId: 'encode-decode', subcategoryId: 'unicode-encode-decode', component: WorkInProgress },
  'hex-encode-decode': { categoryId: 'encode-decode', subcategoryId: 'hex-encode-decode', component: WorkInProgress },
  'binary-encode-decode': { categoryId: 'encode-decode', subcategoryId: 'binary-encode-decode', component: WorkInProgress },
  'ascii-encode-decode': { categoryId: 'encode-decode', subcategoryId: 'ascii-encode-decode', component: WorkInProgress },
  'morse-code-encode-decode': { categoryId: 'encode-decode', subcategoryId: 'morse-code-encode-decode', component: WorkInProgress },
  'jwt-encode-decode': { categoryId: 'encode-decode', subcategoryId: 'jwt-encode-decode', component: WorkInProgress },
  'json-encode-decode': { categoryId: 'encode-decode', subcategoryId: 'json-encode-decode', component: WorkInProgress },
  'xml-encode-decode': { categoryId: 'encode-decode', subcategoryId: 'xml-encode-decode', component: WorkInProgress },
  'csv-encode-decode': { categoryId: 'encode-decode', subcategoryId: 'csv-encode-decode', component: WorkInProgress },

  // Generator
  'password-generator': { categoryId: 'generator', subcategoryId: 'password-generator', component: PasswordGenerator },
  'random-password-generator': { categoryId: 'generator', subcategoryId: 'random-password-generator', component: RandomPasswordGenerator },
  'secure-password-generator': { categoryId: 'generator', subcategoryId: 'secure-password-generator', component: SecurePasswordGenerator },
  'api-key-generator': { categoryId: 'generator', subcategoryId: 'api-key-generator', component: ApiKeyGenerator },
  'uuid-generator': { categoryId: 'generator', subcategoryId: 'uuid-generator', component: UuidGenerator },
  'oauth-token-generator': { categoryId: 'generator', subcategoryId: 'oauth-token-generator', component: OAuthTokenGenerator },
  'random-string-generator': { categoryId: 'generator', subcategoryId: 'random-string-generator', component: RandomStringGenerator },
  'qr-code-generator': { categoryId: 'generator', subcategoryId: 'qr-code-generator', component: QRCodeGenerator },
  'barcode-generator': { categoryId: 'generator', subcategoryId: 'barcode-generator', component: BarcodeGenerator },
  'md5-hash-generator': { categoryId: 'generator', subcategoryId: 'md5-hash-generator', component: MD5HashGenerator },
  'sha1-hash-generator': { categoryId: 'generator', subcategoryId: 'sha1-hash-generator', component: SHA1HashGenerator },
  'sha256-hash-generator': { categoryId: 'generator', subcategoryId: 'sha256-hash-generator', component: SHA256HashGenerator },
  'sha384-hash-generator': { categoryId: 'generator', subcategoryId: 'sha384-hash-generator', component: SHA384HashGenerator },
  'sha512-hash-generator': { categoryId: 'generator', subcategoryId: 'sha512-hash-generator', component: SHA512HashGenerator },
  'sha3-hash-generator': { categoryId: 'generator', subcategoryId: 'sha3-hash-generator', component: SHA3HashGenerator },
  'blake2-hash-generator': { categoryId: 'generator', subcategoryId: 'blake2-hash-generator', component: BLAKE2HashGenerator },
  'whirlpool-hash-generator': { categoryId: 'generator', subcategoryId: 'whirlpool-hash-generator', component: WhirlpoolHashGenerator },
  'ripemd-hash-generator': { categoryId: 'generator', subcategoryId: 'ripemd-hash-generator', component: RIPEMDHashGenerator },
  'bcrypt-hash-generator': { categoryId: 'generator', subcategoryId: 'bcrypt-hash-generator', component: BcryptHashGenerator },
  'scrypt-hash-generator': { categoryId: 'generator', subcategoryId: 'scrypt-hash-generator', component: ScryptHashGenerator },
  'argon2-hash-generator': { categoryId: 'generator', subcategoryId: 'argon2-hash-generator', component: Argon2HashGenerator },
  'pbkdf2-hash-generator': { categoryId: 'generator', subcategoryId: 'pbkdf2-hash-generator', component: PBKDF2HashGenerator },
  'rsa-key-generator': { categoryId: 'generator', subcategoryId: 'rsa-key-generator', component: RSAKeyGenerator },
  'ssh-key-generator': { categoryId: 'generator', subcategoryId: 'ssh-key-generator', component: SSHKeyGenerator },

  // Encrypt Decrypt
  'text-encrypt-decrypt': { categoryId: 'encrypt-decrypt', subcategoryId: 'text-encrypt-decrypt', component: TextEncryptDecryptApp },
  'json-encrypt-decrypt': { categoryId: 'encrypt-decrypt', subcategoryId: 'json-encrypt-decrypt', component: JsonEncryptDecryptApp },
  'file-encrypt-decrypt': { categoryId: 'encrypt-decrypt', subcategoryId: 'file-encrypt-decrypt', component: FileEncryptDecrypt },
  'aes-encrypt-decrypt': { categoryId: 'encrypt-decrypt', subcategoryId: 'aes-encrypt-decrypt', component: AESEncryptDecrypt },
  'des-encrypt-decrypt': { categoryId: 'encrypt-decrypt', subcategoryId: 'des-encrypt-decrypt', component: DESEncryptDecrypt },
  '3des-encrypt-decrypt': { categoryId: 'encrypt-decrypt', subcategoryId: '3des-encrypt-decrypt', component: TripleDESEncryptDecrypt },
  'blowfish-encrypt-decrypt': { categoryId: 'encrypt-decrypt', subcategoryId: 'blowfish-encrypt-decrypt', component: BlowfishEncryptDecrypt },
  'twofish-encrypt-decrypt': { categoryId: 'encrypt-decrypt', subcategoryId: 'twofish-encrypt-decrypt', component: TwofishEncryptDecrypt },
  'rc4-encrypt-decrypt': { categoryId: 'encrypt-decrypt', subcategoryId: 'rc4-encrypt-decrypt', component: RC4EncryptDecrypt },
  'chacha20-encrypt-decrypt': { categoryId: 'encrypt-decrypt', subcategoryId: 'chacha20-encrypt-decrypt', component: ChaCha20EncryptDecrypt },
  'rsa-encrypt-decrypt': { categoryId: 'encrypt-decrypt', subcategoryId: 'rsa-encrypt-decrypt', component: RSAEncryptDecrypt },
  'ecc-encrypt-decrypt': { categoryId: 'encrypt-decrypt', subcategoryId: 'ecc-encrypt-decrypt', component: ECCEncryptDecrypt },
  'dsa-encrypt-decrypt': { categoryId: 'encrypt-decrypt', subcategoryId: 'dsa-encrypt-decrypt', component: DSAEncryptDecrypt },
  'elgamal-encrypt-decrypt': { categoryId: 'encrypt-decrypt', subcategoryId: 'elgamal-encrypt-decrypt', component: ElGamalEncryptDecrypt },
  'zip-encrypt-decrypt': { categoryId: 'encrypt-decrypt', subcategoryId: 'zip-encrypt-decrypt', component: ZipEncryptDecrypt },
  'pdf-encrypt-decrypt': { categoryId: 'encrypt-decrypt', subcategoryId: 'pdf-encrypt-decrypt', component: PDFEncryptDecrypt },
  'image-encrypt-decrypt': { categoryId: 'encrypt-decrypt', subcategoryId: 'image-encrypt-decrypt', component: ImageEncryptDecrypt },
  'database-encrypt-decrypt': { categoryId: 'encrypt-decrypt', subcategoryId: 'database-encrypt-decrypt', component: DatabaseEncryptDecrypt },

  // Cipher
  'caesar-cipher': { categoryId: 'cipher', subcategoryId: 'caesar-cipher', component: CaesarCipherApp },
  'vigenere-cipher': { categoryId: 'cipher', subcategoryId: 'vigenere-cipher', component: VigenereCipherApp },
  'atbash-cipher': { categoryId: 'cipher', subcategoryId: 'atbash-cipher', component: AtbashCipherApp },
  'rot13-cipher': { categoryId: 'cipher', subcategoryId: 'rot13-cipher', component: ROT13CipherApp },
  'rot47-cipher': { categoryId: 'cipher', subcategoryId: 'rot47-cipher', component: ROT47CipherApp },
  'playfair-cipher': { categoryId: 'cipher', subcategoryId: 'playfair-cipher', component: PlayfairCipherApp },
  'hill-cipher': { categoryId: 'cipher', subcategoryId: 'hill-cipher', component: HillCipherApp },
  'rail-fence-cipher': { categoryId: 'cipher', subcategoryId: 'rail-fence-cipher', component: RailFenceCipherApp },
  'columnar-transposition': { categoryId: 'cipher', subcategoryId: 'columnar-transposition', component: ColumnarTranspositionCipherApp },
  'affine-cipher': { categoryId: 'cipher', subcategoryId: 'affine-cipher', component: AffineCipherApp },
  'bacon-cipher': { categoryId: 'cipher', subcategoryId: 'bacon-cipher', component: BaconCipherApp },
  'polybius-square': { categoryId: 'cipher', subcategoryId: 'polybius-square', component: PolybiusSquareApp },
  'four-square-cipher': { categoryId: 'cipher', subcategoryId: 'four-square-cipher', component: FourSquareCipherApp },
  'two-square-cipher': { categoryId: 'cipher', subcategoryId: 'two-square-cipher', component: TwoSquareCipherApp },
  'beaufort-cipher': { categoryId: 'cipher', subcategoryId: 'beaufort-cipher', component: BeaufortCipherApp },
  'gronsfeld-cipher': { categoryId: 'cipher', subcategoryId: 'gronsfeld-cipher', component: GronsfeldCipherApp },
  'porta-cipher': { categoryId: 'cipher', subcategoryId: 'porta-cipher', component: PortaCipherApp },

  // Steganography
  'image-steganography': { categoryId: 'steganography', subcategoryId: 'image-steganography', component: ImageSteganographyApp },
  'text-steganography': { categoryId: 'steganography', subcategoryId: 'text-steganography', component: TextSteganographyApp },
  'audio-steganography': { categoryId: 'steganography', subcategoryId: 'audio-steganography', component: WorkInProgress },
  'video-steganography': { categoryId: 'steganography', subcategoryId: 'video-steganography', component: WorkInProgress },
  'pdf-steganography': { categoryId: 'steganography', subcategoryId: 'pdf-steganography', component: WorkInProgress },
  'lsb-steganography': { categoryId: 'steganography', subcategoryId: 'lsb-steganography', component: WorkInProgress },
  'dct-steganography': { categoryId: 'steganography', subcategoryId: 'dct-steganography', component: WorkInProgress },
  'whitespace-steganography': { categoryId: 'steganography', subcategoryId: 'whitespace-steganography', component: WorkInProgress },
  'unicode-steganography': { categoryId: 'steganography', subcategoryId: 'unicode-steganography', component: WorkInProgress },
  'metadata-steganography': { categoryId: 'steganography', subcategoryId: 'metadata-steganography', component: WorkInProgress },

  // Other Security Tools
  'password-strength-checker': { categoryId: 'other-security-tools', subcategoryId: 'password-strength-checker', component: WorkInProgress },
  'hash-cracker': { categoryId: 'other-security-tools', subcategoryId: 'hash-cracker', component: WorkInProgress },
  'hash-identifier': { categoryId: 'other-security-tools', subcategoryId: 'hash-identifier', component: WorkInProgress },
  'digital-signature-verifier': { categoryId: 'other-security-tools', subcategoryId: 'digital-signature-verifier', component: WorkInProgress },
  'certificate-parser': { categoryId: 'other-security-tools', subcategoryId: 'certificate-parser', component: WorkInProgress },
  'ssl-checker': { categoryId: 'other-security-tools', subcategoryId: 'ssl-checker', component: WorkInProgress },
  'checksum-calculator': { categoryId: 'other-security-tools', subcategoryId: 'checksum-calculator', component: WorkInProgress },
  'entropy-calculator': { categoryId: 'other-security-tools', subcategoryId: 'entropy-calculator', component: WorkInProgress },
  'secure-delete': { categoryId: 'other-security-tools', subcategoryId: 'secure-delete', component: WorkInProgress },
  'password-policy-checker': { categoryId: 'other-security-tools', subcategoryId: 'password-policy-checker', component: WorkInProgress },
  'breach-checker': { categoryId: 'other-security-tools', subcategoryId: 'breach-checker', component: WorkInProgress },
  'network-scanner': { categoryId: 'other-security-tools', subcategoryId: 'network-scanner', component: WorkInProgress },
  'vulnerability-scanner': { categoryId: 'other-security-tools', subcategoryId: 'vulnerability-scanner', component: WorkInProgress },
  'malware-scanner': { categoryId: 'other-security-tools', subcategoryId: 'malware-scanner', component: WorkInProgress },
  'phishing-detector': { categoryId: 'other-security-tools', subcategoryId: 'phishing-detector', component: WorkInProgress },
};

// Type definitions
type ConverterComponentsType = {
  [category: string]: {
    [subcategory: string]: React.ComponentType<any>;
  }
};

// Direct route mapping
type DirectRouteMapType = {
  [path: string]: {
    categoryId: string;
    subcategoryId: string;
    component: React.ComponentType<any>;
  }
};

// Loading component
const LoadingFallback = () => (
  <div className="flex items-center justify-center h-full">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
  </div>
);

// Fallback component when no converter is found
const ConverterNotFound = ({ categoryId, subcategoryId }: { categoryId?: string, subcategoryId?: string }) => (
  <div className="max-w-4xl mx-auto">
    <h1 className="text-2xl font-bold mb-6">Converter Not Found</h1>
    <div className="p-4 mb-6 bg-yellow-50 border border-yellow-200 text-yellow-700 rounded-md">
      <p className="mb-2">
        <strong>The requested converter could not be found.</strong>
      </p>
      <p>
        {categoryId && subcategoryId ? 
          `We couldn't find a converter for ${categoryId}/${subcategoryId}.` :
          'No converter was specified in the URL.'}
      </p>
    </div>
  </div>
);

const ToolsData: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [query, setQuery] = useState<string>('');
  const [filteredData, setFilteredData] = useState<Category[]>([]);

  const ScrollToTop = () => {
    useEffect(() => {
      // Scroll to top when component mounts
      window.scrollTo(0, 0);
    }, []);
  
    return null; // This component doesn't render anything
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toLowerCase();
    setQuery(value);

    if (value === '') {
      setFilteredData([]);
      return;
    }

    const results = categories
      .map(category => {
        const matchingSubCategories = category.subCategories.filter(sub =>
          sub.name.toLowerCase().includes(value)
        );
        if (matchingSubCategories.length > 0) {
          return {
            ...category,
            subCategories: matchingSubCategories
          };
        }
        return null;
      })
      .filter((cat): cat is Category => cat !== null);

    setFilteredData(results);
  };

  // Extract path information from URL
  const getRouteInfo = () => {
    // First check for direct paths
    const directPath = location.pathname.split('/').pop() || '';
    const isDirectRoute = directPath in directRouteMap;
    
    if (isDirectRoute) {
      const route = directRouteMap[directPath];
      return {
        isDirectRoute,
        directPath,
        categoryId: route.categoryId,
        subcategoryId: route.subcategoryId
      };
    }
    
    // Fall back to traditional URL structure
    const urlParts = location.pathname.split('/').filter(Boolean);
    
    // Check for /formatter/category/subcategory pattern
    if (urlParts[0] === 'tools' && urlParts.length > 1) {
      return {
        isDirectRoute: false,
        directPath: '',
        categoryId: urlParts[1],
        subcategoryId: urlParts.length > 2 ? urlParts[2] : ''
      };
    }
    
    // Default case - no recognized pattern
    return {
      isDirectRoute: false,
      directPath: '',
      categoryId: '',
      subcategoryId: ''
    };
  };

  const routeInfo = getRouteInfo();
  const { isDirectRoute, directPath, categoryId, subcategoryId } = routeInfo;

  // Debug logs
  useEffect(() => {
    console.log('Current URL:', location.pathname);
    console.log('Route info:', routeInfo);
  }, [location.pathname]);

  // UI States
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);

  // Handle responsive design
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // State for categories with their expansion status
  const [categoriesState, setCategoriesState] = useState<Category[]>(() => {
    // Initialize with categories from the routes file and set isOpen based on current route
    return categories.map(category => ({
      ...category,
      isOpen: category.id === categoryId || Boolean(category.isOpen)
    }));
  });

  // Update categories isOpen state when URL params change
  useEffect(() => {
    setCategoriesState(prevCategories =>
      prevCategories.map(category => ({
        ...category,
        isOpen: category.id === categoryId || Boolean(category.isOpen)
      }))
    );
  }, [categoryId]);

  // Toggle category expansion
  const toggleCategory = (catId: string) => {
    setCategoriesState(prevState =>
      prevState.map(category =>
        category.id === catId
          ? { ...category, isOpen: !Boolean(category.isOpen) }
          : category
      )
    );
  };

  // Navigate to a subcategory
  const navigateToSubcategory = (categoryId: string, subcategoryId: string, name: string) => {
    trackEvent('Convert Page', 'Click', 'Navigate to Subcategory');
    pushGTMEvent({
      event: 'convert_page',
      category: 'Click Subcategory',
      action: 'Click',
      label: name,
    });

    // Check if a direct route exists for this subcategory
    const directRoute = Object.entries(directRouteMap).find(
      ([_, value]) => value.categoryId === categoryId && value.subcategoryId === subcategoryId
    );

    if (directRoute) {
      // Use the simplified URL if we found a mapping
      navigate(`/${directRoute[0]}`);
    } else {
      // Fall back to the traditional URL structure
      navigate(`/tools/${categoryId}/${subcategoryId}`);
    }

    // Close sidebar on mobile after navigation
    if (isMobile) {
      setSidebarOpen(false);
    }
  };

  // Find current selected items
  const {
    category: currentCategory = null,
    subcategory: currentSubcategory = null
  } = findCategoryAndSubcategory(categoryId, subcategoryId) || {};

  // Function to check if a category is selected
  const isCategorySelected = (category: Category) => {
    return category.id === categoryId;
  };

  // Function to check if a subcategory is selected
  const isSubcategorySelected = (catId: string, subCatId: string) => {
    // Check both URL patterns
    return (
      (location.pathname.includes(`/tools/${catId}/${subCatId}`)) ||
      Object.entries(directRouteMap).some(
        ([path, mapping]) =>
          location.pathname.includes(`/${path}`) &&
          mapping.categoryId === catId &&
          mapping.subcategoryId === subCatId
      )
    );
  };

  // Generate SEO metadata
  const generateSEOMetadata = () => {
    if (currentCategory && currentSubcategory) {
      const toolName = currentSubcategory.name;
      const pageTitle = `${toolName} - Free Online Data Conversion Tool`;

      let description = "";
      if (toolName.includes(" to ")) {
        const [from, to] = toolName.split(" to ");
        description = `Convert ${from} data to ${to} format online for free. Easy-to-use ${from} to ${to} converter with no installation required.`;
      } else if (toolName.includes("Formatter")) {
        const format = toolName.split(" ")[0];
        description = `Format and beautify your ${format} data with our free online ${format} formatter. Clean, indent, and structure your ${format} for better readability.`;
      } else {
        description = `Work with ${currentCategory.name} data using our free online ${toolName} tool. No signup required, instant results.`;
      }

      const keywords = `${toolName.toLowerCase()}, ${toolName.toLowerCase().replace(" to ", " converter, ")}, ${currentCategory.name.toLowerCase()} converter, online converter`;

      return {
        title: pageTitle,
        description: description,
        keywords: keywords,
        canonicalUrl: window.location.href,
        ogTitle: pageTitle,
        ogDescription: description,
        ogType: "website",
        twitterCard: "summary_large_image",
        twitterTitle: pageTitle,
        twitterDescription: description
      };
    } else if (currentCategory) {
      // Category page
      const pageTitle = `${currentCategory.name} Converters - Free Online Tools`;
      const description = `Free online tools to convert ${currentCategory.name} data to various formats. Convert, format and transform your ${currentCategory.name} data with ease.`;
      const keywords = `${currentCategory.name.toLowerCase()} converters, ${currentCategory.name.toLowerCase()} formatter, ${currentCategory.name.toLowerCase()} tools, online converter`;

      return {
        title: pageTitle,
        description: description,
        keywords: keywords,
        canonicalUrl: window.location.href,
        ogTitle: pageTitle,
        ogDescription: description
      };
    }

    // Default for home page
    return {
      title: "Data Converters - Free Online Data Format Conversion Tools",
      description: "Transform your data between various formats including JSON, CSV, SQL, XML, and YAML with our free online data converters.",
      keywords: "data converter, json to csv, csv to json, sql formatter, xml formatter, yaml converter",
      canonicalUrl: window.location.href
    };
  };

  const seoProps = generateSEOMetadata();

  // Render the main content area based on the current state
  const renderMainContent = () => {
    console.log('Rendering main content with:', { categoryId, subcategoryId, isDirectRoute, directPath });

    // First check if we're using a direct route
    if (isDirectRoute && directPath in directRouteMap) {
      const route = directRouteMap[directPath];
      if (route.component) {
        const DirectComponent = route.component;
        return <DirectComponent />;
      }
    }

    // Otherwise fall back to the category/subcategory pattern
    if (categoryId && subcategoryId && categoryId in converterComponents) {
      // Check if the converter exists in our component map
      const converterCategory = converterComponents[categoryId];
      if (subcategoryId in converterCategory) {
        const ConverterComponent = converterCategory[subcategoryId];
        return <ConverterComponent />;
      } else {
        console.warn(`Converter not found for ${categoryId}/${subcategoryId}`);
        return <ConverterNotFound categoryId={categoryId} subcategoryId={subcategoryId} />;
      }
    }

    // Default home content or category landing page
    return (
      <div className="min-h-screen from-gray-50 to-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Search bar */}
          <div className="max-w-lg mx-auto mb-12 relative">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search size={20} className="text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search data converters..."
                value={query}
                onChange={handleInputChange}
                className="w-full pl-10 pr-4 py-3 text-base rounded-xl border border-gray-300 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white transition-all duration-200"
              />
            </div>

            {filteredData?.length > 0 && (
              <div className="absolute mt-2 w-full bg-white border border-gray-200 rounded-xl shadow-lg z-10 overflow-hidden">
                {filteredData?.map(category => (
                  <div key={category.id} className="border-b border-gray-100 last:border-b-0">
                    <div className="px-4 py-3 bg-gray-50">
                      <span className="font-medium text-gray-800">{category.name}</span>
                    </div>
                    <ul className="py-2">
                      {category?.subCategories.map(sub => (
                        <li
                          key={sub.id}
                          className="px-4 py-2 hover:bg-blue-50 flex items-center justify-between cursor-pointer group"
                          onClick={() => {
                            setQuery('');
                            navigate(`/${sub?.id}`);
                          }}
                        >
                          <span className="text-gray-700 group-hover:text-blue-700 transition-colors">
                            {sub.name}
                          </span>
                          <ChevronRight size={16} className="text-gray-400 group-hover:text-blue-500 transition-colors" />
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Main content */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-extrabold text-gray-800 mb-4 tracking-tight">
              {currentCategory ? `${currentCategory.name}` : 'Data Conversion Tools'}
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {currentCategory
                ? `Transform your ${currentCategory.name.toLowerCase()} quickly and easily with our conversion tools.`
                : 'Simple, powerful tools to convert your data between formats. No installation required.'}
            </p>
          </div>

          {!currentCategory ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {categories.map(category => (
                <div
                  key={category.id}
                  className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-md transition duration-300 cursor-pointer"
                  onClick={() => {
                    trackEvent('Convert Page', 'Click', 'Navigate to Category');
                    navigate(`/tools/${category.id}`);
                  }}
                >
                  <div className="p-6">
                    <h2 className="text-xl font-semibold text-gray-800 mb-2">{category.name}</h2>
                    <p className="text-gray-500 mb-4">{category.subCategories.length} conversion tools</p>

                    <div className="space-y-1 mb-4">
                      {category.subCategories.slice(0, 2).map(sub => (
                        <div key={sub.id} className="text-sm text-gray-600 flex items-center">
                          <div className="w-1 h-1 rounded-full bg-gray-400 mr-2"></div>
                          {sub.name}
                        </div>
                      ))}
                      {category.subCategories.length > 2 && (
                        <div className="text-sm text-gray-500">
                          +{category.subCategories.length - 2} more tools
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="bg-gray-50 px-6 py-3 flex justify-end">
                    <div className="flex items-center text-blue-600 font-medium text-sm group">
                      <span className="group-hover:underline">Explore</span>
                      <ArrowRight size={16} className="ml-1 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row max-w-6xl mx-auto">
              <div className="max-w-6xl mx-auto">
                <div className="mb-6">
                  <h2 className="text-2xl font-semibold text-gray-800">{currentCategory.name}</h2>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {currentCategory.subCategories.map(sub => (
                    <div
                      key={sub.id}
                      className="bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-200 cursor-pointer"
                      onClick={() => navigateToSubcategory(currentCategory.id, sub.id, sub.name)}
                    >
                      <div className="p-4 flex items-center justify-between">
                        <span className="text-gray-700 font-medium">{sub.name}</span>
                        <ChevronRight size={18} className="text-gray-400" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Features section */}
          {!currentCategory && (
            <div className="mt-24 text-center">
              <h2 className="text-2xl font-bold text-gray-800 mb-12">Why Choose Our Conversion Tools?</h2>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                  <div className="bg-blue-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <h3 className="font-semibold text-gray-800 mb-2">Fast & Efficient</h3>
                  <p className="text-gray-600 text-sm">Convert your data instantly with our optimized tools. No waiting or processing delays.</p>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                  <div className="bg-blue-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  <h3 className="font-semibold text-gray-800 mb-2">Secure & Private</h3>
                  <p className="text-gray-600 text-sm">All conversions happen in your browser. Your data never leaves your device.</p>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                  <div className="bg-blue-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                  </div>
                  <h3 className="font-semibold text-gray-800 mb-2">100% Free</h3>
                  <p className="text-gray-600 text-sm">All our conversion tools are completely free to use with no hidden fees or subscriptions.</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
    <ScrollToTop />
      <SEO {...seoProps} />
      <div className="flex h-full bg-gray-100 relative">
        {/* Sidebar - controlled by sidebarOpen state */}
        <div
          className={`
            bg-white shadow-md overflow-y-auto
            transition-all duration-300 ease-in-out
            ${sidebarOpen ? 'w-64' : 'w-0 overflow-hidden'}
          `}
        >
          <div className="h-full">
            <nav className="py-2">
              {categoriesState.map(category => (
                <div key={category.id} className="mb-1">
                  <button
                    className={`
                      flex items-center justify-between w-full px-4 py-3 text-left
                      ${isCategorySelected(category) ? 'text-blue-600 font-medium' : 'text-gray-700'}
                      hover:bg-gray-50 focus:outline-none
                    `}
                    onClick={() => toggleCategory(category.id)}
                  >
                    <span className="text-base">{category.name}</span>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className={`
                        h-4 w-4 transition-transform duration-200 ease-in-out
                        ${category.isOpen ? 'transform rotate-180' : ''}
                      `}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {/* Subcategories with smooth height animation */}
                  <div
                    className={`
                      overflow-hidden transition-all duration-300 ease-in-out
                      ${Boolean(category.isOpen) ? 'max-h-full opacity-100' : 'max-h-0 opacity-0'}
                    `}
                  >
                    {category.subCategories.map(subCategory => (
                      <button
                        key={subCategory.id}
                        className={`
                          w-full text-left py-3 px-8 text-sm
                          ${isSubcategorySelected(category.id, subCategory.id)
                            ? 'text-blue-600 font-medium bg-blue-50'
                            : 'text-gray-700 hover:bg-gray-50'
                          }
                          focus:outline-none transition-colors
                        `}
                        onClick={() => navigateToSubcategory(category.id, subCategory.id, subCategory.name)}
                      >
                        {subCategory.name}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </nav>
          </div>
        </div>

        {/* Main content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <header className="bg-white shadow-sm z-10 relative">
            {/* Breadcrumbs */}
            {currentCategory && (
              <div className="flex items-center justify-between px-4 py-3 bg-gray-100 border-t border-b text-sm relative">

                {/* Toggle Button on left */}
                <button
                  onClick={() => {
                    trackEvent('Convert Page', 'Click', 'Toggle Sidebar');
                    setSidebarOpen(!sidebarOpen);
                  }}
                  aria-label={sidebarOpen ? "Close sidebar" : "Open sidebar"}
                >
                  {sidebarOpen ? (
                    // Close Icon (X)
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  ) : (
                    // Menu Icon (Hamburger)
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="3" y1="12" x2="21" y2="12" />
                      <line x1="3" y1="6" x2="21" y2="6" />
                      <line x1="3" y1="18" x2="21" y2="18" />
                    </svg>
                  )}
                </button>

                {/* Breadcrumb Text */}
                <div className="flex items-center space-x-2 ml-4 flex-1 overflow-hidden">
                  <span className="text-gray-500 truncate animate-slide-in">CATEGORY</span>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                  <span className="text-gray-700 truncate">{currentCategory.name}</span>

                  {currentSubcategory && (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                      <span className="text-blue-600 font-medium truncate">{currentSubcategory.name}</span>
                    </>
                  )}
                </div>
              </div>
            )}
          </header>

          <main className="flex-1 overflow-auto p-6">
            {renderMainContent()}
          </main>
        </div>
      </div>
    </>
  );
};

export default ToolsData;